import { EditPencil, Trash } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { ResourceCard, ResourceCardLogo, ResourceCardMenu } from 'components/blocks/resource-card';
import { ProjectFolderIcon } from 'components/blocks/layout/project-folder-icon';
import type { Project } from 'features/projects/types/create-project';

interface ProjectCardProps {
  project: Project;
  onSelect: (project: Project) => void;
  onRenameClick: (project: Project) => void;
  onDeleteClick: (project: Project) => void;
}

export function ProjectCard({ project, onSelect, onRenameClick, onDeleteClick }: ProjectCardProps) {
  const { t } = useTranslation();
  const name = project.name || t('UNTITLED_PROJECT', { defaultValue: 'Untitled project' });
  const description =
    project.instructions?.trim() || t('NO_PROJECT_INSTRUCTIONS', { defaultValue: 'No instructions yet' });

  return (
    <ResourceCard
      title={name}
      description={description}
      icon={
        <ResourceCardLogo name={name} className="bg-grey-50 dark:bg-true-white/10">
          <ProjectFolderIcon color={project.color} />
        </ResourceCardLogo>
      }
      onSelect={() => onSelect(project)}
      actions={
        <ResourceCardMenu
          ariaLabel={t('MORE_ACTIONS') || 'More actions'}
          items={[
            {
              label: t('RENAME') || 'Rename',
              icon: <EditPencil width={24} height={24} strokeWidth={1.5} />,
              onClick: () => onRenameClick(project),
            },
            {
              label: t('DELETE') || 'Delete',
              icon: <Trash width={20} height={20} strokeWidth={1.5} />,
              onClick: () => onDeleteClick(project),
              destructive: true,
            },
          ]}
        />
      }
    />
  );
}
